"use client";
import {
    useBreakpointStore,
    type BreakpointKeys,
    type BreakpointState,
} from "../stores/breakpointStore";

/**
 * Hook for accessing a single breakpoint from the UI breakpoint store
 * @param breakpoint - The breakpoint key to access
 * @returns boolean indicating if the breakpoint is active
 */
export const useBreakpoint = <T extends BreakpointKeys>(breakpoint: T): boolean => {
    return useBreakpointStore((state) => state.breakpoints[breakpoint]);
};

/**
 * Hook for accessing multiple breakpoints from the UI breakpoint store
 * @param breakpoints - Array of breakpoint keys to access
 * @returns Object with the specified breakpoints as boolean values
 */
export const useBreakpoints = <T extends BreakpointKeys[]>(
    breakpoints: T
): Pick<BreakpointState, T[number]> => {
    return useBreakpointStore((state) => {
        const result = {} as any;
        breakpoints.forEach((bp) => {
            result[bp] = state.breakpoints[bp];
        });
        return result;
    });
};

/**
 * Hook to check if viewport is currently resizing
 * @returns boolean indicating if viewport is in resizing state
 */
export const useIsResizing = (): boolean => {
    return useBreakpointStore((state) => state.breakpoints.isResizing);
};

/**
 * Hook to get all breakpoint states (for backward compatibility)
 * @returns Complete breakpoint state object
 */
export const useAllBreakpoints = (): BreakpointState => {
    return useBreakpointStore((state) => state.breakpoints);
};

// Individual breakpoint hooks for common usage patterns
export const useSmBreakpoint = () => useBreakpoint("sm");
export const useMdBreakpoint = () => useBreakpoint("md");
export const useLgBreakpoint = () => useBreakpoint("lg");
export const useXlBreakpoint = () => useBreakpoint("xl");
export const use2xlBreakpoint = () => useBreakpoint("2xl");
export const use3xlBreakpoint = () => useBreakpoint("3xl");
export const use4xlBreakpoint = () => useBreakpoint("4xl");
export const use5xlBreakpoint = () => useBreakpoint("5xl");
export const useBaseBreakpoint = () => useBreakpoint("base");
